import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from '@env';

declare global {
  interface Window {
    myLandbot: any;
    Landbot: any;
  }
}

@Injectable({ providedIn: 'root' })
export class ScriptLoaderService {
  private renderer: Renderer2;
  private scriptElement: HTMLScriptElement | null = null;
  private targetElement: HTMLElement | null = null;
  private languageSubscription: Subscription | null = null;
  private landbotInitialized = false;
  private currentLanguage = '';
  private pendingOpen = false;
  private isOpen = false;
  private widgetObserver: MutationObserver | null = null;
  private readonly styleId = 'landbot-sidebar-position-style';
  private readonly dockClass = 'landbot-sidebar-positioned';
  private dockTimer: ReturnType<typeof setInterval> | null = null;
  private displayMode: 'sidebar' | 'login' = 'sidebar';

  constructor(rendererFactory: RendererFactory2, private translate: TranslateService) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  loadScript(targetElement: HTMLElement, mode: 'sidebar' | 'login' = 'sidebar'): void {
    //if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return;
    const modeChanged = this.displayMode !== mode;
    if (modeChanged && this.landbotInitialized) this.destroyLandbot();
    this.displayMode = mode;
    this.targetElement = targetElement;
    this.currentLanguage = this.getCurrentLanguage();
    this.installDockStyles();
    this.observeWidget();
    this.updateDockSize();
    this.loadLandbotLibrary();

    if (!this.languageSubscription) {
      this.languageSubscription = this.translate.onLangChange.subscribe(event => {
        const newLanguage = this.normalizeLanguage(event.lang);
        if (newLanguage !== this.currentLanguage) {
          this.currentLanguage = newLanguage;
          this.changeLandbotLanguage();
        }
      });
    }
  }

  openChat(targetElement: HTMLElement): void {
    this.pendingOpen = true;
    this.targetElement = targetElement;
    this.displayMode = 'sidebar';
    this.installDockStyles();
    if (this.landbotInitialized && window.myLandbot) {
      this.openPendingChat();
      return;
    }
    this.loadScript(targetElement);
  }

  private openPendingChat(): void {
    if (!this.pendingOpen || !window.myLandbot) return;
    this.pendingOpen = false;
    this.isOpen = true;
    if (this.displayMode === 'sidebar') this.restorePanelPosition();
    try {
      window.myLandbot.open();
      // Landbot may create its widget after open() returns.
      // The observer applies the sidebar-panel class as soon as it appears.
    } catch (error) {
      this.isOpen = false;
      this.updateDockSize();
      console.warn('Landbot could not open', error);
    }
  }

  private installDockStyles(): void {
    document.getElementById(this.styleId)?.remove();
    const style = this.renderer.createElement('style') as HTMLStyleElement;
    style.id = this.styleId;
    style.textContent = `
      body.landbot-sidebar-mode .LandbotLivechat:not(.landbot-sidebar-panel) {
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
      body.landbot-sidebar-mode .LandbotLivechat.landbot-sidebar-panel {
        position: fixed !important;
        left: 8px !important;
        right: auto !important;
        top: auto !important;
        bottom: 12px !important;
        transform: none !important;
        width: min(453px, calc(100vw - 16px)) !important;
        min-width: 0 !important;
        height: min(600px, calc(100dvh - 24px)) !important;
        min-height: 0 !important;
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        z-index: 10000 !important;
      }
      body.landbot-sidebar-mode .LandbotLivechat.landbot-sidebar-panel > iframe {
        position: absolute !important;
        left: 0 !important;
        top: 0 !important;
        right: auto !important;
        bottom: auto !important;
        width: 100% !important;
        height: 100% !important;
        transform: none !important;
      }
    `;
    this.renderer.appendChild(document.head, style);
    document.body.classList.toggle('landbot-sidebar-mode', this.displayMode === 'sidebar');
  }

  updateDockSize(): void {
    const widget = document.querySelector<HTMLElement>('.LandbotLivechat');
    if (!widget) return;
    widget.classList.toggle('landbot-sidebar-panel', this.displayMode === 'sidebar' && this.isOpen);
  }

  private restorePanelPosition(): void {
    this.updateDockSize();
  }

  private observeWidget(): void {
    if (this.widgetObserver) return;
    this.widgetObserver = new MutationObserver(() => {
      const widget = document.querySelector<HTMLElement>('.LandbotLivechat');
      if (widget && this.displayMode === 'sidebar') {
        const shouldShow = this.isOpen;
        if (widget.classList.contains('landbot-sidebar-panel') !== shouldShow) {
          widget.classList.toggle('landbot-sidebar-panel', shouldShow);
        }
      }
    });
    this.widgetObserver.observe(document.body, { childList: true, subtree: true });
  }

  private loadLandbotLibrary(): void {
    if (window.Landbot) {
      this.initializeLandbot();
      return;
    }
    const existingScript = document.querySelector('script[src*="cdn.landbot.io/landbot-3/landbot-3.0.0.mjs"]') as HTMLScriptElement;
    if (existingScript) {
      existingScript.addEventListener('load', () => this.initializeLandbot());
      return;
    }
    this.scriptElement = this.renderer.createElement('script');
    this.scriptElement.type = 'module';
    this.scriptElement.src = 'https://cdn.landbot.io/landbot-3/landbot-3.0.0.mjs';
    this.scriptElement.onload = () => this.initializeLandbot();
    this.scriptElement.onerror = error => console.warn('Landbot script failed to load', error);
    this.renderer.appendChild(document.body, this.scriptElement);
  }

  private initializeLandbot(): void {
    if (this.landbotInitialized) return;
    if (!window.Landbot) {
      console.warn('Landbot is not available');
      return;
    }
    const configUrl = this.currentLanguage === 'fr'
      ? 'https://storage.googleapis.com/landbot.pro/v3/H-3504587-AY1R7TOGFI0JSGCD/index.json'
      : 'https://storage.googleapis.com/landbot.pro/v3/H-3502394-9KG2VVK9CWUV2ZUU/index.json';

    window.myLandbot = new window.Landbot.Livechat({ configUrl });
    window.myLandbot.core.events.on('widget_open', () => {
      this.isOpen = true;
      if (this.displayMode === 'sidebar') this.restorePanelPosition();
      this.resizeLandbotWidget(true);
      this.restoreCustomControls();
    });
    window.myLandbot.core.events.on('widget_close', () => {
      this.isOpen = false;
      this.resizeLandbotWidget(false);
      this.updateDockSize();
    });
    this.landbotInitialized = true;
    this.isOpen = false;
    this.resizeLandbotWidget(false);
    this.updateDockSize();
    this.openPendingChat();
  }

  private changeLandbotLanguage(): void {
    const reopen = this.isOpen;
    this.destroyLandbot();
    this.pendingOpen = reopen;
    setTimeout(() => this.initializeLandbot(), 100);
  }

  private getCurrentLanguage(): string {
    return this.normalizeLanguage(this.translate.currentLang || this.translate.defaultLang || 'en');
  }

  private normalizeLanguage(language: string): string {
    return language && language.toLowerCase().startsWith('fr') ? 'fr' : 'en';
  }

  private restoreCustomControls(): void {
    if (!window.myLandbot || !window.myLandbot.window) return;
    const doc = window.myLandbot.window.document;
    const inputContainer = doc.querySelector('.input-container') as HTMLElement;
    if (inputContainer) {
      inputContainer.style.display = 'flex';
      inputContainer.style.flexDirection = 'column';
      inputContainer.style.gap = '10px';
      inputContainer.style.alignItems = 'stretch';
    }
    const workflow = doc.getElementById('workflow-container') as HTMLElement;
    if (workflow) {
      workflow.style.display = 'flex';
      workflow.style.gap = '8px';
      workflow.style.width = '100%';
    }
    doc.querySelectorAll('.workflow-btn').forEach((element: Element) => {
      const btn = element as HTMLElement;
      btn.style.flex = '1';
      btn.style.height = '42px';
      btn.style.border = 'none';
      btn.style.borderRadius = '6px';
      btn.style.background = '#015391';
      btn.style.color = '#fff';
      btn.style.fontSize = '14px';
      btn.style.fontWeight = '600';
      btn.style.cursor = 'pointer';
    });
    const inputRow = doc.getElementById('input-row') as HTMLElement;
    if (inputRow) {
      inputRow.style.display = 'flex';
      inputRow.style.alignItems = 'center';
      inputRow.style.gap = '10px';
      inputRow.style.width = '100%';
    }
    const inputWrapper = doc.querySelector('.input-wrapper') as HTMLElement;
    if (inputWrapper) {
      inputWrapper.style.display = 'block';
      inputWrapper.style.flex = '8';
    }
    const inputGroup = doc.querySelector('.input-group') as HTMLElement;
    if (inputGroup) {
      inputGroup.style.display = 'flex';
      inputGroup.style.position = 'relative';
      inputGroup.style.width = '100%';
    }
    const input = doc.querySelector('.input_dynamic') as HTMLElement;
    if (input) {
      input.style.display = 'block';
      input.style.width = '100%';
    }
    const sendButton = doc.querySelector('.send-button') as HTMLElement;
    if (sendButton) sendButton.style.display = 'block';
    const agentBtn = doc.getElementById('talk-agent-btn') as HTMLElement;
    if (agentBtn) {
      agentBtn.style.flex = '2 1 0%';
      agentBtn.style.height = '55px';
      agentBtn.style.border = 'none';
      agentBtn.style.borderRadius = '6px';
      agentBtn.style.background = 'rgb(1, 83, 145)';
      agentBtn.style.color = 'rgb(255, 255, 255)';
      agentBtn.style.fontSize = '14px';
      agentBtn.style.fontWeight = '600';
      agentBtn.style.cursor = 'pointer';
      agentBtn.style.whiteSpace = 'nowrap';
    }
    const hint = doc.querySelector('.enter-hint') as HTMLElement;
    if (hint) hint.style.display = window.innerWidth < 768 ? 'none' : 'block';
  }

  private resizeLandbotWidget(isOpen: boolean): void {
    const widget = document.querySelector('.LandbotLivechat') as HTMLElement;
    if (!widget) return;
    if (isOpen) {
      this.updateDockSize();
      widget.style.visibility = 'visible';
      widget.style.width = '453px';
      widget.style.height = '600px';
      widget.style.minWidth = '453px';
      widget.style.minHeight = '600px';
    } else {
      widget.style.visibility = 'visible';
      widget.style.width = '100px';
      widget.style.height = '100px';
      widget.style.minWidth = '100px';
      widget.style.minHeight = '100px';
    }
  }

  private destroyLandbot(): void {
    this.isOpen = false;
    if (window.myLandbot) {
      try {
        if (window.myLandbot.destroy) window.myLandbot.destroy();
      } catch (error) {
        console.warn('Landbot cleanup error', error);
      }
      window.myLandbot = null;
    }
    document.querySelectorAll('.LandbotLivechat, iframe[src*="landbot"]').forEach(element => element.parentNode?.removeChild(element));

    this.landbotInitialized = false;
  }

  removeScript(targetElement: HTMLElement): void {
    if (this.targetElement !== targetElement) return;
    if (this.dockTimer) clearInterval(this.dockTimer);
    this.dockTimer = null;
    this.widgetObserver?.disconnect();
    this.widgetObserver = null;
    this.destroyLandbot();
    this.languageSubscription?.unsubscribe();
    this.languageSubscription = null;
    if (this.scriptElement?.parentNode) this.scriptElement.parentNode.removeChild(this.scriptElement);
    this.scriptElement = null;
    this.targetElement = null;
    this.currentLanguage = '';
    this.pendingOpen = false;
    document.body.classList.remove('landbot-sidebar-mode');
  }
}
